<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useProductCatalogLauncher } from 'dashboard/composables/useProductCatalogLauncher';

const { content, contentAttributes } = useMessageContext();
const { t } = useI18n();
const { updateUISettings } = useUISettings();
const { requestProductDetail } = useProductCatalogLauncher();

const catalog = computed(() => contentAttributes.value?.catalog || {});
const products = computed(() => {
  if (contentAttributes.value?.products?.length) {
    return contentAttributes.value.products;
  }
  // The catalog panel sends one product per message (content_attributes.product,
  // singular) rather than an array.
  return contentAttributes.value?.product
    ? [contentAttributes.value.product]
    : [];
});
const orderItems = computed(
  () =>
    contentAttributes.value?.order?.products ||
    contentAttributes.value?.cart?.products ||
    []
);
const title = computed(() => {
  if (orderItems.value.length)
    return t('CONVERSATION.PRODUCT_CATALOG.CART_MESSAGE');
  return (
    catalog.value.name || t('CONVERSATION.PRODUCT_CATALOG.MESSAGE_CONTENT')
  );
});

const productName = product =>
  product.name ||
  product.title ||
  product.productRetailerId ||
  product.product_retailer_id;

const productPrice = product =>
  product.price || product.itemPrice || product.item_price;

const productImage = product => product.imageUrl || product.image_url;

const openProductDetail = product => {
  requestProductDetail(product);
  updateUISettings({
    is_product_catalog_panel_open: true,
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
    is_olist_panel_open: false,
  });
};
</script>

<template>
  <BaseBubble class="w-80 max-w-full px-4 py-3">
    <!-- Products sent from the catalog panel already show their own thumb,
    name and price in the card below, so the generic cart icon/title here
    would just duplicate that. Only show it for an actual multi-item
    cart/order message, where it adds real context. -->
    <div
      v-if="orderItems.length && !products.length"
      class="mb-3 flex items-center gap-2"
    >
      <span
        class="grid size-8 place-items-center rounded-md bg-n-blue-4 text-n-blue-11"
      >
        <span class="i-lucide-shopping-cart size-4" />
      </span>
      <div class="min-w-0">
        <p class="truncate text-sm font-medium">
          {{ title }}
        </p>
        <p v-if="content" class="truncate text-xs text-n-slate-11">
          {{ content }}
        </p>
      </div>
    </div>

    <ul v-if="products.length || orderItems.length" class="space-y-2">
      <li
        v-for="product in products.length ? products : orderItems"
        :key="
          product.id ||
          product.retailerId ||
          product.retailer_id ||
          product.productRetailerId ||
          product.product_retailer_id
        "
        class="flex cursor-pointer gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-2 shadow-sm transition-colors hover:border-n-blue-8"
        @click="openProductDetail(product)"
      >
        <img
          v-if="productImage(product)"
          :src="productImage(product)"
          :alt="productName(product)"
          class="size-14 shrink-0 rounded-md object-cover"
        />
        <span
          v-else
          class="grid size-14 shrink-0 place-content-center rounded-md bg-n-alpha-2 text-n-slate-10"
        >
          <span class="i-lucide-package size-5" />
        </span>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-medium text-n-slate-12">
            {{ productName(product) }}
          </p>
          <p
            v-if="productPrice(product)"
            class="mt-1 text-sm font-semibold text-n-slate-12"
          >
            {{ product.currency }} {{ productPrice(product) }}
          </p>
        </div>
        <span v-if="product.quantity" class="shrink-0 text-xs text-n-slate-11">
          {{ `x${product.quantity}` }}
        </span>
      </li>
    </ul>
    <p v-else-if="content" class="text-sm text-n-slate-12">
      {{ content }}
    </p>
  </BaseBubble>
</template>
